import {
  mdiCallMerge,
  mdiCallSplit,
  mdiCancel,
  mdiCashRefund,
  mdiCheck,
  mdiPackageVariantClosed,
  mdiProgressClock,
  mdiSourceBranch,
  mdiTimerOffOutline
} from '@mdi/js'

import { formatSlaDateTime } from '@/modules/company/Approvals/PriorityPayments/slaFormat'
import type {
  AffiliatePackageDetailResponse,
  AffiliatePackageRow,
  AffiliateTimelineEvent
} from '@/Network/Types/Responses/AntecipeAffiliateResponses'

import { formatPackageIdList } from './format'
import { PACKAGE_STAGE_LABELS, nextStage } from './labels'

export type TimelineTone = 'done' | 'warn' | 'pending'

export interface TimelineEntry {
  key: string
  icon: string
  title: string
  when: string
  description: string
  linkPackageId: number | null
  tone: TimelineTone
}

const instantOf = (event: AffiliateTimelineEvent): string | null =>
  event.at ?? event.detectedAt ?? null

/** Evento sem instante (Antecipe não informou e o sync não registrou) vai para o fim. */
const sortKey = (event: AffiliateTimelineEvent): number => {
  const iso = instantOf(event)
  const ms = iso ? Date.parse(iso) : Number.NaN
  return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY
}

const formatWhen = (event: AffiliateTimelineEvent): string => {
  const iso = instantOf(event)
  if (!iso || !Number.isFinite(Date.parse(iso))) return ''
  // Sem `at`, o instante é o da detecção pelo sync: sempre aproximado (pacote legado).
  const approx = event.approx || !event.at
  return `${formatSlaDateTime(iso)}${approx ? ' (aprox.)' : ''}`
}

/** Etapa fora do enum (backend mais novo que o web) cai no rótulo genérico, sem lançar. */
const statusChangedTitle = (stage: AffiliateTimelineEvent['stage']): string => {
  const label: string | undefined = stage ? PACKAGE_STAGE_LABELS[stage] : undefined
  return label ? `Entrou em ${label.toLowerCase()}` : 'Mudou de etapa'
}

/** Descrição do CREATED: agrupado > divisão > pedido do médico (spec antecipe-package-moves, mockup T4). */
const createdDescription = (pkg: AffiliatePackageRow): string => {
  if (pkg.mergedFromPackageIds?.length)
    return 'Primeiro pedido do médico neste pacote (vale para o prazo)'
  if (pkg.originPackageId) return `Pacote criado na divisão do #${pkg.originPackageId}`
  return `${pkg.itemCount} ${pkg.itemCount === 1 ? 'plantão enviado' : 'plantões enviados'} pelo médico`
}

/**
 * Origens do agrupamento (plano ME D7): `mergedFromPackageIds` do pacote e, sem ele, os
 * `relatedPackageId` dos eventos MERGED (um por origem), deduplicados na ordem recebida.
 */
const mergeOriginsOf = (pkg: AffiliatePackageRow, events: AffiliateTimelineEvent[]): number[] => {
  if (pkg.mergedFromPackageIds?.length) return pkg.mergedFromPackageIds
  const ids: number[] = []
  events.forEach((event) => {
    const related = event.relatedPackageId
    if (event.type === 'MERGED' && related !== null && !ids.includes(related)) ids.push(related)
  })
  return ids
}

const mergedTitle = (origins: readonly number[]): string => {
  if (!origins.length) return 'Pacotes agrupados neste'
  if (origins.length === 1) return `Reuniu o pacote ${formatPackageIdList(origins)}`
  return `Reuniu os pacotes ${formatPackageIdList(origins)}`
}

const movedInTitle = (relatedPackageId: number | null): string =>
  relatedPackageId ? `Separado do pacote #${relatedPackageId}` : 'Plantão recebido de outro pacote'

const movedOutDescription = (relatedPackageId: number | null): string =>
  relatedPackageId
    ? `Plantão recusado pela empresa foi para o pacote #${relatedPackageId}.`
    : 'Plantão recusado pela empresa saiu deste pacote.'

/** Expirou na conferência da empresa; a volta para a conferência é reabertura (spec antecipe-dashboard §7.2). */
const statusChangedEntry = (
  event: AffiliateTimelineEvent,
  previousStage: AffiliateTimelineEvent['stage']
): Pick<TimelineEntry, 'icon' | 'tone' | 'title'> & { description?: string } => {
  if (event.stage === 'EXPIRED')
    return {
      icon: mdiTimerOffOutline,
      tone: 'warn',
      title: 'Expirou sem análise da empresa',
      description:
        'A empresa não analisou em 10 dias. O Antecipe ainda pode reabrir o pacote em até 30 dias.'
    }
  if (previousStage === 'EXPIRED' && event.stage === 'COMPANY_REVIEW')
    return { icon: mdiCheck, tone: 'done', title: 'Reaberto: voltou para a conferência da empresa' }
  return { icon: mdiCheck, tone: 'done', title: statusChangedTitle(event.stage) }
}

function toEntry(
  event: AffiliateTimelineEvent,
  index: number,
  pkg: AffiliatePackageRow,
  origins: readonly number[],
  previousStage: AffiliateTimelineEvent['stage'] = null
): TimelineEntry {
  const base = {
    key: `${event.type}-${index}`,
    when: formatWhen(event),
    description: '',
    linkPackageId: null as number | null
  }
  switch (event.type) {
    case 'CREATED':
      return {
        ...base,
        icon: mdiPackageVariantClosed,
        tone: 'done',
        title: 'Pedido de antecipação',
        description: createdDescription(pkg)
      }
    case 'STATUS_CHANGED':
      return { ...base, ...statusChangedEntry(event, previousStage) }
    case 'ITEM_MOVED_IN':
      return {
        ...base,
        icon: mdiSourceBranch,
        tone: 'warn',
        title: movedInTitle(event.relatedPackageId),
        description: 'A empresa médica recusou este plantão na conferência do pacote original.',
        linkPackageId: event.relatedPackageId
      }
    case 'ITEM_MOVED_OUT':
      return {
        ...base,
        icon: mdiCallSplit,
        tone: 'warn',
        title: 'Conferência com recusa',
        description: movedOutDescription(event.relatedPackageId),
        linkPackageId: event.relatedPackageId
      }
    case 'PAID':
      return { ...base, icon: mdiCheck, tone: 'done', title: 'Pago' }
    case 'CANCELED':
      return { ...base, icon: mdiCancel, tone: 'pending', title: 'Cancelado' }
    case 'REVERSED':
      return {
        ...base,
        icon: mdiCashRefund,
        tone: 'warn',
        title: 'Pagamento desfeito no Antecipe',
        description:
          'O pacote saiu de Pago depois de contado; o ajuste entra no repasse da competência em que foi detectado.'
      }
    // Origem nunca abre no portal (backend não a devolve), por isso sem link.
    case 'MERGED':
      return { ...base, icon: mdiCallMerge, tone: 'done', title: mergedTitle(origins) }
    default:
      return { ...base, icon: mdiCheck, tone: 'done', title: String(event.type) }
  }
}

export function buildTimeline(detail: AffiliatePackageDetailResponse): TimelineEntry[] {
  const pkg = detail.package
  const sorted = detail.timeline
    .map((event, index) => ({ event, index }))
    .sort((a, b) => sortKey(a.event) - sortKey(b.event) || a.index - b.index)
  const origins = mergeOriginsOf(pkg, detail.timeline)
  // Plano ME D7: N eventos MERGED (um por origem, mesmo instante) viram uma entrada só.
  let mergedShown = false
  const visible = sorted.filter(({ event }) => {
    if (event.type !== 'MERGED') return true
    if (mergedShown) return false
    mergedShown = true
    return true
  })
  // Etapa da mudança de status anterior (na ordem cronológica), para reconhecer a reabertura.
  let previousStage: AffiliateTimelineEvent['stage'] = null
  const entries = visible.map(({ event, index }) => {
    const entry = toEntry(event, index, pkg, origins, previousStage)
    if (event.type === 'STATUS_CHANGED') previousStage = event.stage ?? null
    return entry
  })
  const next = nextStage(pkg.stage)
  if (next) {
    entries.push({
      key: 'next',
      icon: mdiProgressClock,
      title: `Próxima etapa: ${PACKAGE_STAGE_LABELS[next]}`,
      when: '',
      description: '',
      linkPackageId: null,
      tone: 'pending'
    })
  }
  return entries
}
